import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
// @mui
import { styled } from '@mui/material/styles';
import {
  Stack,
  Typography,
  TextField,
  Fade,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
// components
import Iconify from '../../../../components/Iconify';
import DIOText from '../../../../components/DIOText';
// util
import axios from '../../../../utils/axios';
// hooks
import useAuth from '../../../../hooks/useAuth';


const IconStyle = styled(Iconify)(({ theme }) => ({
  width: 20,
  height: 20,
  flexShrink: 0,
  marginRight: theme.spacing(1),
  color: theme.palette.text.disabled,
}));

const FormStyle = styled('div')(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: Number(theme.shape.borderRadius) * 2,
  backgroundColor: theme.palette.background.neutral,
}));

const COUNT_DICT = {
  designer: [
    <Typography key="1">현재 서비스/디자인이 얼마나 제작 되었는지 알려주세요.</Typography>,
    <Typography key="2">주요 업무를 더 나열해서 적어주세요.</Typography>,
    <Typography key="3">자격 요건/우대조건이 있다면 적어주세요.</Typography>,
    <Typography key="4">크루가 업무 분량을 가늠할 수 있는 정보를 추가해주세요.</Typography>,
  ],
  marketer: [
    <Typography key="1">지금까지 마케팅을 어떻게 진행하셨나요?</Typography>,
    <Typography key="2">회사 내 마케터와 디자이너가 있나요?</Typography>,
    <Typography key="3"><DIOText /> 마케터와 일하며 어떤 목표를 이루고 싶으신가요?</Typography>,
    <Typography key="4">자격조건/우대사항을 알려주세요.</Typography>,
  ],
  contents: [
    <Typography key="1">어떤 채널에 올라갈 콘텐츠가 필요하신가요? (홈페이지, 인스타그램, 블로그 주소)</Typography>,
    <Typography key="2">콘텐츠의 레퍼런스가 있다면 링크를 붙여주세요.</Typography>,
    <Typography key="3">자격조건/우대사항을 알려주세요.</Typography>,
  ],
  performance: [
    <Typography key="1">무엇을 마케팅해야 하나요? (서비스 이름, 사이트 주소)</Typography>,
    <Typography key="2">월 마케팅 예산이 얼마인가요?</Typography>,
    <Typography key="3">현재 어떤 채널에서 광고를 하고 계신가요?</Typography>,
    <Typography key="4">어떤 채널에서 광고 하기를 희망하시나요?</Typography>,
    <Typography key="5">얼마나 잦은 정기 보고를 원하시나요? (1주/2주)</Typography>,
  ]
};

const SCOPE_DICT = {
  designer: [
    <Typography key="1">SNS 운영 및 관리</Typography>,
    <Typography key="2">영상/사진 촬영 및 편집</Typography>,
    <Typography key="3">패션/헤어 디자인</Typography>,
  ],
  marketer: [
    <Typography key="1">영업</Typography>,
    <Typography key="2">MD</Typography>,
    <Typography key="3">디자인</Typography>,
  ],
}

export default function SelfCheckForm({jobPost, stepName, next}) {
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [taskContent, setTaskContent] = useState(jobPost?.task_for_4weeks);
  const [isSubmit, setIsSubmit] = useState(false);

  let countContent;
  if (jobPost?.detail_role?.role === '콘텐츠 마케터') {
    countContent = COUNT_DICT['contents'];
  } else if (jobPost?.detail_role?.role == '퍼포먼스 마케터') {
    countContent = COUNT_DICT['performance'];
  } else {
    countContent = COUNT_DICT[jobPost?.position] || [];
  }

  const positionEmoji = jobPost?.position === "designer" ? "🎨" : "💸";
  let scopeContent = SCOPE_DICT[jobPost?.position] || [];

  useEffect(() => {
    setTaskContent(jobPost?.task_for_4weeks);
  }, []);

  const handleTaskContent = (e) => {
    setTaskContent(e.target.value);
  };

  const check = async (checkContent) => {
    try {
      setIsSubmit(true);
      const companyId = user?.employee.company[0].id;
      const jobPostId = jobPost?.id;
      const checkData = {
        check_type: 'check',
        check_content: checkContent,
        tasks: taskContent,
      };
      const response = await axios.post(
        `/api/company/${companyId}/job-posts/${jobPostId}/self-check/`,
        checkData
      )
      enqueueSnackbar('저장되었습니다');
      next();
      setIsSubmit(false);
    } catch (error) {
      console.error(error);
      setIsSubmit(false);
      enqueueSnackbar('처리중 오류가 발생하였습니다. 관리자에게 문의해주세요', { variant: 'error' });
    }
  }

  return (
    <>
    {stepName === '글자수 확인' && (
      <Stack sx={{ mb: 1 , flexWrap: 'wrap', gap: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ px: 1 }}>
          더 좋은 매칭을 위해 작성하신 공고를 점검해주세요.
        </Typography>

        <FormStyle>
          <Typography variant="h6">
            ✏️  공고를 30자 이상으로 적어주세요.
          </Typography>
          <Typography variant="body2" paragraph>
            아래 내용을 참고해 공고를 더 상세하게 적어주세요.
          </Typography>

          <Stack spacing={1}>
            {countContent.map((cc, index) => (
              <Stack direction="row" alignItems="initial" key={index}>
                <IconStyle icon={"ic:outline-check-circle"} />
                {cc}
              </Stack>
            ))}
          </Stack>
        </FormStyle>

        <TextField
          label="크루에게 요청할 업무"
          multiline
          minRows={5}
          value={taskContent}
          helperText={'공고가 제대로 작성되지 않으면 후보가 매칭되지 않을 수 있어요. 최대한 꼼꼼하게 점검해주세요!'}
          onChange={handleTaskContent}
        />

        <LoadingButton
          fullWidth
          size="large"
          onClick={() => check("count")}
          variant="contained"
          loading={isSubmit}
        >
          30자 이상으로 작성했어요
        </LoadingButton>
      </Stack>
    )}
    {stepName === '포지션 확인' && (
      <Stack sx={{ mb: 1 , flexWrap: 'wrap', gap: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ px: 1 }}>
          더 좋은 매칭을 위해 작성하신 공고를 점검해주세요.
        </Typography>

        <Fade in={stepName === '포지션 확인'} {...(stepName === '포지션 확인' ? { timeout: 500 } : {})}>
        <FormStyle>
          <Typography variant="h6">
            🔀 업무가 섞이지 않도록 적어주세요.
          </Typography>
          <Typography variant="body2" paragraph>
            선택하신 포지션은 {jobPost?.detail_role?.role} 입니다.
          </Typography>
          <Stack>
            <Stack direction="row" alignContent="center">
              <IconStyle icon={"ic:outline-check-circle"} sx={{ color: 'success.main' }} />
              <Typography variant="subtitle1" gutterBottom>
                이런 일을 합니다
              </Typography>
            </Stack>
            <Typography sx={{ whiteSpace: 'pre-wrap' }} paragraph>
              {jobPost?.detail_role?.can}
            </Typography>
          </Stack>
          {jobPost?.detail_role?.cant && (
            <Stack>
              <Stack direction="row" alignContent="center">
                <IconStyle icon={"ic:outline-do-not-disturb-alt"} sx={{ color: 'error.main' }} />
                <Typography variant="subtitle1" gutterBottom>
                  이런일은 할 수 없어요
                </Typography>
              </Stack>
              <Typography sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
                {jobPost?.detail_role?.cant}
              </Typography>
            </Stack>
          )}
        </FormStyle>
        </Fade>

        <TextField
          label="크루에게 요청할 업무"
          multiline
          minRows={5}
          value={taskContent}
          helperText={'공고가 제대로 작성되지 않으면 후보가 매칭되지 않을 수 있어요. 최대한 꼼꼼하게 점검해주세요!'}
          onChange={handleTaskContent}
        />

        <LoadingButton
          fullWidth
          size="large"
          onClick={() => check("position")}
          variant="contained"
          loading={isSubmit}
        >
          섞이지 않게 적었어요
        </LoadingButton>
      </Stack>
    )}
    {stepName === '업무범위 확인' && (
      <Stack sx={{ mb: 1 , flexWrap: 'wrap', gap: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ px: 1 }}>
          더 좋은 매칭을 위해 작성하신 공고를 점검해주세요.
        </Typography>

        <Fade in={stepName === '업무범위 확인'} {...(stepName === '업무범위 확인' ? { timeout: 400 } : {})}>
        <FormStyle>
          <Typography variant="h6" paragraph>
            {positionEmoji} 아래 업무는 {jobPost?.get_position_display}가 수행할 수 없으니 제외해주세요.
          </Typography>
          <Stack spacing={1}>
            {scopeContent.map((sc, index) => (
              <Stack direction="row" alignItems="initial" key={index}>
                <IconStyle icon={"ic:outline-do-not-disturb-alt"} />
                {sc}
              </Stack>
            ))}
          </Stack>
        </FormStyle>
        </Fade>

        <TextField
          label="크루에게 요청할 업무"
          multiline
          minRows={5}
          value={taskContent}
          helperText={'공고가 제대로 작성되지 않으면 후보가 매칭되지 않을 수 있어요. 최대한 꼼꼼하게 점검해주세요!'}
          onChange={handleTaskContent}
        />

        <LoadingButton
          fullWidth
          size="large"
          onClick={() => check("scope")}
          variant="contained"
          loading={isSubmit}
        >
          {jobPost?.get_position_display} 업무만 적었어요
        </LoadingButton>
      </Stack>
    )}
    </>
  )
}
